import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Search, CheckCircle2, XCircle, Loader2, Shield, User, Calendar, CreditCard, AlertTriangle, Clock, X, ZoomIn, Share2, Copy
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatRut = (value) => {
  let rut = value.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rut.length === 0) return '';
  let dv = rut.slice(-1);
  let cuerpo = rut.slice(0, -1);
  let f = '';
  let c = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    f = cuerpo[i] + f;
    c++;
    if (c === 3 && i !== 0) { f = '.' + f; c = 0; }
  }
  if (cuerpo.length === 0) return dv;
  return f + '-' + dv;
};

// mode: 'turno' (only active, simple view) | 'liga' (active + inactive history)
export default function ConsultaIdentidad({ mode = 'turno' }) {
  const [rut, setRut] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [historial, setHistorial] = useState(null);
  const [buscado, setBuscado] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [lightboxLabel, setLightboxLabel] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const enrollmentUrl = `${window.location.origin}/enrolamiento`;

  const handleRutChange = (e) => {
    const val = e.target.value;
    const limpio = val.replace(/[^0-9kK]/g, '');
    if (limpio.length <= 9) setRut(formatRut(val));
  };

  const buscar = async () => {
    const rutLimpio = rut.replace(/[^0-9kK]/g, '').toUpperCase();
    if (rutLimpio.length < 8) { toast.error('Ingresa un RUT valido'); return; }
    setLoading(true);
    setBuscado(true);
    setResultado(null);
    setHistorial(null);
    try {
      if (mode === 'liga') {
        const res = await axios.get(`${API}/enrolamiento/historial/${rutLimpio}`, { withCredentials: true });
        setHistorial(res.data);
      } else {
        const nivel = mode === 'turno' ? 'turno' : 'admin';
        const res = await axios.get(`${API}/enrolamiento/consultar/${rutLimpio}?nivel=${nivel}`, { withCredentials: true });
        setResultado(res.data);
      }
    } catch { toast.error('Error al consultar'); }
    finally { setLoading(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') buscar(); };

  const limpiar = () => { setRut(''); setResultado(null); setHistorial(null); setBuscado(false); };

  const renderPersonCard = (persona, isActive) => {
    const isAdmin = mode === 'liga';
    return (
    <Card className={`p-4 ${isActive ? 'bg-slate-900/80 border-emerald-500/30' : 'bg-slate-900/50 border-slate-700 opacity-70'}`}
      data-testid={isActive ? 'consulta-resultado-activo' : 'consulta-resultado-inactivo'}>
      <div className="flex gap-4">
        <div className="flex-shrink-0 flex gap-3">
          {/* Foto cara - siempre visible */}
          {persona.foto_cara ? (
            <div className="text-center">
              <div className="relative cursor-pointer group" onClick={() => { setLightboxImg(persona.foto_cara); setLightboxLabel('Selfie'); }}
                data-testid="consulta-foto-tap">
                <img src={persona.foto_cara} alt="Foto" data-testid="consulta-foto"
                  className={`w-28 h-36 object-cover rounded-lg border-2 ${isActive ? 'border-emerald-500/50' : 'border-slate-600'} transition-transform group-active:scale-95`} />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg flex items-center justify-center transition-all">
                  <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Selfie</p>
            </div>
          ) : (
            <div className="w-28 h-36 rounded-lg border-2 border-slate-700 bg-slate-800 flex items-center justify-center">
              <User className="w-10 h-10 text-slate-600" />
            </div>
          )}
          {/* Foto cedula - solo admin/liga */}
          {isAdmin && persona.foto_cedula && (
            <div className="text-center">
              <div className="relative cursor-pointer group" onClick={() => { setLightboxImg(persona.foto_cedula); setLightboxLabel('Cedula'); }}
                data-testid="consulta-cedula-tap">
                <img src={persona.foto_cedula} alt="Cedula" data-testid="consulta-cedula"
                  className={`w-28 h-36 object-cover rounded-lg border-2 ${isActive ? 'border-blue-500/50' : 'border-slate-600'} transition-transform group-active:scale-95`} />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg flex items-center justify-center transition-all">
                  <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-80 transition-opacity" />
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">Cedula</p>
            </div>
          )}
        </div>

        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={isActive
              ? (persona.verificacion_facial ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30')
              : 'bg-slate-600/20 text-slate-400 border-slate-600/30'}>
              {isActive ? (
                persona.verificacion_facial
                  ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Verificado</>
                  : <><XCircle className="w-3 h-3 mr-1" /> Sin verificacion</>
              ) : (
                'Inactivo'
              )}
            </Badge>
            {!isActive && <Badge className="bg-red-500/10 text-red-400 border-red-500/30 text-xs">Desactivado</Badge>}
            {isAdmin && persona.estado_verificacion === 'pendiente_verificacion_manual' && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs" data-testid="badge-pendiente-verificacion">
                <Clock className="w-3 h-3 mr-1" /> Pendiente Verificacion
              </Badge>
            )}
            {isAdmin && persona.estado_verificacion === 'rechazado' && (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs" data-testid="badge-rechazado">
                <AlertTriangle className="w-3 h-3 mr-1" /> Rechazado
              </Badge>
            )}
          </div>

          <h4 className="text-lg font-bold text-white" data-testid="consulta-nombre">
            {persona.nombre} {persona.apellido || ''}
          </h4>

          <div className="space-y-1 text-sm">
            {persona.rut && (
              <div className="flex items-center gap-2 text-slate-300">
                <CreditCard className="w-3.5 h-3.5 text-slate-500" />
                <span data-testid="consulta-rut-result">{persona.rut || formatRut(rut)}</span>
              </div>
            )}
            {persona.fecha_nacimiento && (
              <div className="flex items-center gap-2 text-slate-300">
                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                <span>{persona.fecha_nacimiento}</span>
                {isAdmin && persona.fecha_nacimiento_modificada && (
                  <span className="text-xs text-amber-400 ml-1">(modificada)</span>
                )}
              </div>
            )}
            {persona.fecha_enrolamiento && (
              <div className="text-xs text-slate-400">
                Enrolado: {new Date(persona.fecha_enrolamiento).toLocaleDateString('es-CL')}
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
    );
  };

  return (
    <>
    <div className="space-y-4" data-testid="consulta-identidad">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-5 h-5 text-emerald-400" />
        <h3 className="font-semibold text-white">
          {mode === 'liga' ? 'Control de Identidad (Historial)' : 'Consulta de Identidad'}
        </h3>
      </div>

      <div className="flex gap-2">
        <Input value={rut} onChange={handleRutChange} onKeyDown={handleKeyDown}
          placeholder="Buscar por RUT..." className="bg-slate-800 border-slate-700 text-white flex-1"
          data-testid="consulta-rut-input" />
        <Button onClick={buscar} disabled={loading || rut.replace(/[^0-9kK]/g, '').length < 8}
          className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="consulta-buscar-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
        {buscado && (
          <Button onClick={limpiar} variant="outline" className="border-slate-700 text-slate-300"
            data-testid="consulta-limpiar-btn">Limpiar</Button>
        )}
      </div>

      {mode === 'liga' && (
        <Button variant="outline" className="w-full border-dashed border-slate-600 text-slate-300 hover:bg-slate-700 gap-2"
          data-testid="share-enrollment-link"
          onClick={async () => {
            if (navigator.clipboard && window.isSecureContext) {
              try {
                await navigator.clipboard.writeText(enrollmentUrl);
                toast.success('Link de enrolamiento copiado');
                return;
              } catch {}
            }
            setShowShareDialog(true);
          }}>
          <Share2 className="w-4 h-4" /> Compartir link de enrolamiento
        </Button>
      )}

      {loading && (
        <div className="flex items-center justify-center p-6">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
        </div>
      )}

      {/* Turno mode: single active result */}
      {buscado && !loading && mode === 'turno' && resultado && (
        <>
          {resultado.encontrado
            ? renderPersonCard(resultado, true)
            : (
              <Card className="bg-slate-900/80 border-slate-700 p-6 text-center" data-testid="consulta-no-encontrado">
                <XCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
                <p className="text-slate-300 font-medium">RUT no encontrado</p>
                <p className="text-sm text-slate-500 mt-1">Esta persona no esta enrolada en el sistema.</p>
              </Card>
            )}
        </>
      )}

      {/* Liga mode: full history */}
      {buscado && !loading && mode === 'liga' && historial && (
        <>
          {historial.total_registros > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-400">
                {historial.total_registros} registro{historial.total_registros > 1 ? 's' : ''} encontrado{historial.total_registros > 1 ? 's' : ''} para <strong className="text-white">{historial.rut}</strong>
              </p>
              {historial.registros.map((reg, idx) => (
                <div key={reg.id || idx}>
                  {renderPersonCard(reg, reg.activo)}
                </div>
              ))}
            </div>
          ) : (
            <Card className="bg-slate-900/80 border-slate-700 p-6 text-center" data-testid="consulta-no-encontrado">
              <XCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
              <p className="text-slate-300 font-medium">RUT no encontrado</p>
              <p className="text-sm text-slate-500 mt-1">Esta persona no esta enrolada en el sistema.</p>
            </Card>
          )}
        </>
      )}
    </div>

    {/* Dialog para compartir link de enrolamiento */}
    {showShareDialog && (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowShareDialog(false)}>
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}
          data-testid="share-dialog">
          <div className="flex items-center gap-2 mb-3">
            <Share2 className="w-5 h-5 text-emerald-400" />
            <h3 className="text-white font-semibold">Link de Enrolamiento</h3>
          </div>
          <p className="text-sm text-slate-400 mb-4">Comparte este link para que se puedan enrolar:</p>
          <div className="bg-slate-900 p-3 rounded-lg border border-slate-600 mb-4">
            <input type="text" readOnly value={enrollmentUrl}
              className="w-full bg-transparent text-blue-300 text-sm font-mono outline-none select-all"
              onClick={e => e.target.select()} data-testid="enrollment-link-input" />
          </div>
          <div className="flex gap-2">
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              onClick={async () => {
                try { await navigator.clipboard.writeText(enrollmentUrl); toast.success('Link copiado'); setShowShareDialog(false); } catch { toast.info('Selecciona el link y copialo manualmente'); }
              }} data-testid="copy-enrollment-link">
              <Copy className="w-4 h-4" /> Copiar
            </Button>
            {navigator.share && (
              <Button variant="outline" className="flex-1 border-slate-600 text-slate-300 gap-2"
                onClick={() => { navigator.share({ title: 'Enrolamiento Kinosis', text: 'Enrolate aqui:', url: enrollmentUrl }).catch(() => {}); }}
                data-testid="native-share-btn">
                <Share2 className="w-4 h-4" /> Compartir
              </Button>
            )}
          </div>
        </div>
      </div>
    )}

    {/* Lightbox fullscreen para ver fotos en grande (mobile-friendly) */}
    {lightboxImg && (
      <div className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center"
        onClick={() => setLightboxImg(null)} data-testid="lightbox-overlay">
        <button className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          onClick={() => setLightboxImg(null)} data-testid="lightbox-close">
          <X className="w-6 h-6 text-white" />
        </button>
        <p className="text-white/60 text-sm mb-3">{lightboxLabel}</p>
        <img src={lightboxImg} alt={lightboxLabel}
          className="max-w-[95vw] max-h-[85vh] object-contain rounded-lg"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-image" />
        <p className="text-white/40 text-xs mt-3">Toca fuera para cerrar</p>
      </div>
    )}
    </>
  );
}
